import { zodResolver } from '@hookform/resolvers/zod';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';

import { changeDisplayName } from '@/api/account';
import { isApiStatus } from '@/api/auth';
import { AppDialog, InlineNotice, TextField } from '@/components/common';
import { Button } from '@/components/ui/button';
import { notify } from '@/lib/toast';
import { useAuthStore } from '@/stores/auth';

import { DISPLAY_NAME_MAX, displayNameSchema, type DisplayNameValues } from '../schemas';

interface DisplayNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * 표시 이름 변경 창(specs/053 US2) — 워크스페이스 사용자 카드·계정 메뉴 공용.
 * 저장 즉시 인증 스토어 이름을 갱신해 카드·메뉴에 새로 고침 없이 반영한다.
 */
export function DisplayNameDialog({ open, onOpenChange }: DisplayNameDialogProps) {
  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="표시 이름 변경"
      description="워크스페이스에 보이는 이름입니다. 실명이나 사무소명을 권장합니다."
      size="sm"
    >
      {/* 열 때마다 현재 이름으로 폼을 새로 만든다 — 닫았다 열면 입력 중이던 값·오류가 남지 않게 */}
      {open && <DisplayNameForm onDone={() => onOpenChange(false)} />}
    </AppDialog>
  );
}

function DisplayNameForm({ onDone }: { onDone: () => void }) {
  const formId = useId();
  const name = useAuthStore((s) => s.name);
  const setName = useAuthStore((s) => s.setName);
  const [serverError, setServerError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<DisplayNameValues>({
    resolver: zodResolver(displayNameSchema),
    defaultValues: { displayName: name ?? '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerError('');
    try {
      const profile = await changeDisplayName(values.displayName);
      setName(profile.displayName);
      notify.success('표시 이름을 바꿨습니다.');
      onDone();
    } catch (error) {
      if (isApiStatus(error, 400)) setServerError(error.message);
      else throw error;
    }
  });

  return (
    <>
      <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
        <TextField
          label="표시 이름"
          maxLength={DISPLAY_NAME_MAX}
          autoComplete="name"
          error={errors.displayName?.message}
          {...register('displayName')}
        />
        {serverError && <InlineNotice tone="destructive">{serverError}</InlineNotice>}
      </form>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>
          취소
        </Button>
        <Button type="submit" form={formId} disabled={isSubmitting}>
          저장
        </Button>
      </div>
    </>
  );
}
